import { MoonfallCloud } from './convex-client.js';
/**
 * Client-side attribution candidate capture.
 * Server authorization governs any committed record.
 */
const MoonfallAttribution = (function () {
  var FIRST_KEY = 'moonfall_attr_first';
  var LAST_KEY = 'moonfall_attr_last';
  var UTM = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  var CLICK_IDS = ['gclid', 'msclkid', 'fbclid', 'ttclid', 'twclid', 'li_fat_id'];

  function readRawFromLocation() {
    var raw = {};
    try {
      var params = new URLSearchParams(window.location.search || '');
      UTM.concat(CLICK_IDS).forEach(function (k) {
        var v = params.get(k);
        if (v) raw[k] = v;
      });
    } catch (e) { /* no URLSearchParams / bad URL */ }

    var ref = '';
    try { ref = document.referrer || ''; } catch (e) { ref = ''; }
    if (ref) {
      // Drop internal (same-origin) navigations — they are not acquisition.
      try {
        if (new URL(ref).host === window.location.host) ref = '';
      } catch (e) { /* keep ref if it can't be parsed */ }
    }
    if (ref) raw.referrer = ref;

    try { raw.landingPath = window.location.pathname || '/'; } catch (e) { /* ignore */ }
    return raw;
  }

  // A bare landingPath is not an acquisition signal on its own.
  function hasSignal(raw) {
    if (!raw) return false;
    return Object.keys(raw).some(function (k) { return k !== 'landingPath' && raw[k]; });
  }

  function safeSet(key, obj) {
    try { sessionStorage.setItem(key, JSON.stringify(obj)); } catch (e) { /* storage blocked */ }
  }
  function safeGet(key) {
    try {
      var s = sessionStorage.getItem(key);
      return s ? JSON.parse(s) : null;
    } catch (e) { return null; }
  }
  function safeRemove(key) {
    try { sessionStorage.removeItem(key); } catch (e) { /* ignore */ }
  }

  // The tagged URL is for us, not for the player. Once the touch is stored the
  // params are only noise in the address bar — and they ride along into
  // anything the player copies, bookmarks or shares. Only the attribution
  // allowlist is removed: the path, the hash, and every other param (room,
  // voucher, lang, listing) belong to somebody else and are left alone.
  function cleanUrl() {
    if (!window.history || typeof window.history.replaceState !== 'function') return;
    var url;
    try {
      url = new URL(window.location.href);
    } catch (e) { return; }

    var removed = false;
    UTM.concat(CLICK_IDS).forEach(function (key) {
      if (!url.searchParams.has(key)) return;
      url.searchParams.delete(key);
      removed = true;
    });
    if (!removed) return; // an untagged URL is never rewritten

    try {
      var search = url.searchParams.toString();
      window.history.replaceState(
        window.history.state,
        '',
        url.pathname + (search ? '?' + search : '') + url.hash,
      );
    } catch (e) { /* history blocked — the touch is already stored */ }
  }

  function capture() {
    var raw = readRawFromLocation();
    if (!hasSignal(raw)) return;
    if (!safeGet(FIRST_KEY)) safeSet(FIRST_KEY, raw); // first touch of the session: set-once
    safeSet(LAST_KEY, raw); // last touch: always refreshed
    // Store first, tidy second: a blocked history API must never cost a touch.
    cleanUrl();
  }

  function getPending() {
    return { firstTouch: safeGet(FIRST_KEY), lastTouch: safeGet(LAST_KEY) };
  }

  function clearPending() {
    safeRemove(FIRST_KEY);
    safeRemove(LAST_KEY);
  }

  async function commit() {
    var pending = getPending();
    if (!pending.firstTouch && !pending.lastTouch) return { success: false, reason: 'no_pending' };
    if (!MoonfallCloud || !MoonfallCloud.attribution) {
      return { success: false, reason: 'cloud_unavailable' };
    }
    try {
      return await MoonfallCloud.attribution.record(
        pending.firstTouch || undefined,
        pending.lastTouch || undefined,
      );
    } catch (e) {
      if (window.CLIENT_DEBUG_MODE) console.warn('[Attribution] commit failed:', e);
      return { success: false, reason: 'error' };
    }
  }

  // Capture as early as possible — the referrer and ad params are only present
  // on the entry navigation.
  capture();

  return { capture: capture, getPending: getPending, clearPending: clearPending, commit: commit };
})();

export { MoonfallAttribution };
